import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import * as yup from "yup";
import { Row, Col, Image, Card, Form, Button, Modal } from "react-bootstrap";
import CheckIcon from "@mui/icons-material/Check";
import { authHandler } from "../redux/handlers";

const STEPS = {
  FORM: 1,
  SUCCESS: 2,
};

const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required("กรุณากรอกรหัสผ่าน")
    .min(6, "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร"),
  confirm_password: yup
    .string()
    .required("กรุณายืนยันรหัสผ่าน")
    .oneOf([yup.ref("password"), null], "รหัสผ่านไม่ตรงกัน"),
});

const SuccessModal = ({ show, onHide, message }) => {
  useEffect(() => {
    if (show) {
      const t = setTimeout(onHide, 2000);
      return () => clearTimeout(t);
    }
  }, [show, onHide]);

  return (
    <Modal show={show} onHide={onHide} size="sm" centered backdrop="static" keyboard={false}>
      <Modal.Body>
        <div className="px-3 py-4">
          <div className="flex flex-col items-center text-center mb-4">
            <svg width="84" height="83" viewBox="0 0 84 83" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="42" cy="41.5" r="41.5" fill="#2DC76D" />
              <path d="M56.0123 30.9902L36.7444 50.2581L27.9863 41.5" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-center fs-22 tc-7">{message}</div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export function ResetPassword() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(STEPS.FORM);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const handleResetSuccess = () => {
    setShowSuccessModal(true);
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setStep(STEPS.SUCCESS);
  };

  return (
    <div className="m-0 w-100 p-0 bg-white">
      <Row className="g-0 h-100vh">
        <Col xl={5} lg={6} className="d-none d-lg-block">
          <Image src="/images/cover_login.png" className="img-fit" />
        </Col>
        <Col xl={7} lg={6} className="d-flex flex-column py-4">
          <div className="mb-auto"></div>
          <Card className="card-login-st-1 mb-auto h-auto">
            <Card.Body>
              <div className="text-center">
                <Image src="/logo.png" width={150} className="mb-4 mx-auto" />
              </div>
              <div className="text-center text-[#FF731D] font-bold text-[24px] fw-5 mb-4">
                ADMIN SYSTEM
              </div>

              {step === STEPS.FORM ? (
                <ResetPasswordForm
                  email={email}
                  token={token}
                  onSuccess={handleResetSuccess}
                  dispatch={dispatch}
                />
              ) : (
                <ResetPasswordSuccess />
              )}
            </Card.Body>
          </Card>
          <div className="text-center fs-20 tc-3 d-flex align-items-end pt-4 justify-content-center">
            <span className="me-3">Power By</span>
            <div className="pb-05">
              <Image src="/images/logo_allder.png" width={98} />
            </div>
          </div>
        </Col>
      </Row>

      <SuccessModal
        show={showSuccessModal}
        onHide={handleModalClose}
        message="กำหนดรหัสผ่านเรียบร้อยแล้ว"
      />
    </div>
  );
}

const ResetPasswordForm = ({ email, token, onSuccess, dispatch }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (values, { setFieldError }) => {
    if (!token) {
      setFieldError("general", "ไม่พบ Token สำหรับการรีเซ็ตรหัสผ่าน");
      return;
    }
    setIsLoading(true);
    try {
      await dispatch(authHandler.resetPassword(token, values.password));
      onSuccess();
    } catch (error) {
      setFieldError("general", "เกิดข้อผิดพลาดในการรีเซ็ตรหัสผ่าน กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center fw-5 fs-20 mb-4">กำหนดรหัสผ่านใหม่</div>
      <Formik
        initialValues={{ email: email || "", password: "", confirm_password: "" }}
        enableReinitialize
        validationSchema={resetPasswordSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, handleChange, handleBlur, touched, errors, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            {errors.general && (
              <div className="alert alert-danger mb-3">{errors.general}</div>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="mb-1">อีเมล</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={values.email}
                readOnly
                disabled
                className="form-st-1 bg-light"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="mb-1">
                รหัสผ่านใหม่ <span className="text-danger">*</span>
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="รหัสผ่าน (อย่างน้อย 6 ตัวอักษร)"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.password && !errors.password && values.password}
                  isInvalid={touched.password && !!errors.password}
                  className="form-st-1"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  className="btn btn-change-type-password"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                />
              </div>
              {touched.password && errors.password && (
                <div className="text-danger small mt-1">{errors.password}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="mb-1">
                ยืนยันรหัสผ่านใหม่ <span className="text-danger">*</span>
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  placeholder="ยืนยันรหัสผ่าน"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.confirm_password && !errors.confirm_password && values.confirm_password}
                  isInvalid={touched.confirm_password && !!errors.confirm_password}
                  className="form-st-1"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  className="btn btn-change-type-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                />
              </div>
              {touched.confirm_password && errors.confirm_password && (
                <div className="text-danger small mt-1">{errors.confirm_password}</div>
              )}
            </Form.Group>

            <div className="d-flex gap-2">
              <Link to="/login" className="btn btn-st-2 w-50">
                ยกเลิก
              </Link>
              <Button
                type="submit"
                className="btn-login w-50"
                disabled={!values.password || !values.confirm_password || isLoading || !token}
              >
                {isLoading ? "กำลังดำเนินการ..." : "ยืนยัน"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

const ResetPasswordSuccess = () => (
  <div className="text-center">
    <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#2DC76D", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <CheckIcon style={{ fontSize: 48, color: "white" }} />
    </div>
    <div className="fw-bold fs-20 mb-4">กำหนดรหัสผ่านสำเร็จ</div>
    <Link to="/login" className="btn btn-login w-100">
      เข้าสู่ระบบ
    </Link>
  </div>
);
